import type {
  MoneySchema,
  JackpotEventScheduleSchema,
  JackpotEventsSchema,
  JackpotSchema,
  PublicCampaignSchema,
} from "@/types";

/**
 * The assembled jackpot campaign view — `campaigns` ⋈ `jackpot_progress`.
 *
 * Composed from the generated schemas rather than re-declared (CAMPAIGN-ARCHITECTURE.md, "Rely on
 * the generated backend types"), so a backend change surfaces here as a type error. Everything
 * structural (title, kind, ceiling, schedule) is the `JackpotSchema` embedded on the campaign;
 * everything that moves (the pot, the deadline, the last payout) is the progress stream's
 * `{active, next, last}` events, attached per jackpot.
 */
export interface JackpotCampaign {
  campaign: PublicCampaignSchema;
  minimum_bet: { value: number; currency: string; display?: string };
  /** Matches `toGameNames`' return: "ALL" is a distinct case from a list. */
  eligible_games: string[] | "ALL";
  eligible_games_count?: number;
  /** The campaign's jackpots in the operator's `display_order`, one to four of them. */
  jackpots: JackpotView[];
  /**
   * Rows for the sidebar "Latest wins" card, newest first; `[]` until somebody has won, and the
   * card renders nothing then. From the progress stream's `recent_wins` (masked name as the lead,
   * amount · title as the detail); against a backend that predates that tail they are derived
   * from each jackpot's `last` event instead — amount and title, no player — so `player` is
   * `null` and `name` carries the amount.
   */
  recent_wins: JackpotWinRow[];
  /** The pots added up, server-formatted like the pots themselves (the progress stream's
   *  `total`); `null` until the stream has pushed or while no pot has state. */
  total: MoneySchema | null;
}

/** One card: the structural definition plus its volatile events (null until the stream pushes). */
export interface JackpotView {
  jackpot: JackpotSchema;
  events: JackpotEventsSchema | null;
}

/** One row of the latest-wins feed, in the shape the type-agnostic `LatestWinsCard` renders,
 *  plus which jackpot it belongs to so a card can find its own newest win. */
export interface JackpotWinRow {
  key: string;
  jackpotId: number;
  /** The platform's opaque player hash, so the reader's own row can say "You"; null when unknown. */
  player: string | null;
  name: string;
  detail?: string;
  wonAt: string;
}

/**
 * The three kinds, by their backend codes (`common/constants.py::JackpotTypes`). `jackpot_type` is
 * a plain string on the wire, so it is narrowed here and nowhere else.
 */
export type JackpotKind = "PR" | "TD" | "PB";

export type JackpotCardState =
  | "live"
  | "mustWin"
  | "cooldown"
  | "justWon"
  | "finished";

/** How long after a payout the card keeps saying "just won" — long enough to explain why the pot
 *  collapsed to its base, short enough not to celebrate over a pot that has already regrown. */
export const JUST_WON_MS = 2 * 60 * 1000;
/** A timed pot under this much time to its deadline shows `m:ss` in gold. */
export const TIMED_SOON_MS = 5 * 60 * 1000;
/** …and under this much shows red seconds. */
export const TIMED_FINAL_MS = 60 * 1000;

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;

/** The kind, or `null` for a code this build does not know — rendered as a progressive (no meter). */
export function jackpotKind(jackpot: JackpotSchema): JackpotKind | null {
  const t = jackpot.jackpot_type;
  return t === "PR" || t === "TD" || t === "PB" ? t : null;
}

const parseMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The card's state. A finished campaign beats everything; cooldown is the timed-only gap between
 * a payout and the next slot starting; justWon is time-boxed on the last payout; a paid-out pot
 * with no event after it is finished on its own; otherwise live.
 *
 * `now` is a parameter so the rule stays pure — the clock is supplied by `useJackpotClock`, never
 * read here, so `assemble.ts` can call nothing time-dependent.
 */
export function jackpotCardState(
  view: JackpotView,
  campaign: PublicCampaignSchema,
  now: number = Date.now()
): JackpotCardState {
  if (campaign.status === "FI") return "finished";
  const active = view.events?.active ?? null;
  const last = view.events?.last ?? null;
  // The backend flags the upcoming slot `is_active` as soon as the previous one pays out, with
  // `activated` still ahead (and no `next` row) — until it starts, that IS the cooldown.
  const upcoming = nextRoundStartMs(view, now);
  if (
    upcoming !== null &&
    upcoming > now &&
    (!active || parseMs(active.activated)! > now)
  ) {
    return "cooldown";
  }
  // A running timed slot past its deadline has not been won yet — the backend forces the fall
  // on the next qualifying bet (`get_probability_for_timed` returns 1). That is its own state:
  // not a dead countdown, not a cooldown, but "must be won now".
  const deadline = parseMs(active?.payout_before);
  if (deadline !== null && deadline <= now) return "mustWin";
  const paidAt = parseMs(last?.payout_date);
  if (paidAt !== null && now - paidAt <= JUST_WON_MS) return "justWon";
  // Paid out with nothing scheduled after it: the backend creates the next event on payout, so
  // no `active` and no `next` means there is none to create — a timed pot whose timespan has run
  // out while the campaign goes on. That jackpot is over; the card says so instead of "live".
  if (!active && !view.events?.next && last?.payout_date) return "finished";
  return "live";
}

/** The event whose pot the card shows: the live one, else the queued next slot (its pot is the
 *  base value the round will start from), else the last payout (a finished campaign's frozen
 *  figure). `null` before the stream has pushed. */
export function displayedEvent(
  view: JackpotView
): JackpotEventScheduleSchema | null {
  return view.events?.active ?? view.events?.next ?? view.events?.last ?? null;
}

/** The server-rendered pot string in the viewer's currency and language, or `null` while unknown.
 *  Always this `display`, never a re-format of `value` — the backend already localised it. */
export function potDisplay(view: JackpotView): string | null {
  return displayedEvent(view)?.jackpot_value.display ?? null;
}

/**
 * How far the pot is toward its ceiling, 0..1 — pays-before only. `null` when there is no ceiling
 * or no live pot, so the meter is not drawn then. A progressive may carry a `max_payout_amount`
 * too, but there it is the operator's safety cap far above the expected payout, not the pitch:
 * the card sells a progressive as "keeps growing", never as "won before X".
 */
export function ceilingFraction(view: JackpotView): number | null {
  const max = view.jackpot.max_payout_amount;
  const active = view.events?.active;
  if (jackpotKind(view.jackpot) !== "PB") return null;
  if (!max || !active || max.value <= 0) return null;
  return Math.min(1, Math.max(0, active.jackpot_value.value / max.value));
}

/** Milliseconds until the live event's deadline (`payout_before`), or `null` when it has none. */
export function msToDeadline(
  view: JackpotView,
  now: number = Date.now()
): number | null {
  const active = view.events?.active;
  const started = parseMs(active?.activated);
  // A slot the backend promoted early has no deadline yet — it has not begun.
  if (started !== null && started > now) return null;
  const deadline = parseMs(active?.payout_before);
  return deadline === null ? null : deadline - now;
}

/** The live event's full slot length (deadline − activation), for the timed ring; `null` when
 *  either end is missing. */
export function slotLengthMs(view: JackpotView): number | null {
  const start = parseMs(view.events?.active?.activated);
  const end = parseMs(view.events?.active?.payout_before);
  if (start === null || end === null || end <= start) return null;
  return end - start;
}

/**
 * When the upcoming round starts, as epoch ms. The backend promotes the next slot to `active` the
 * moment the previous one pays out, with `activated` still ahead — and a daily/weekly pot may
 * ALSO carry a queued `next` slot a whole period later. So an active slot that has not started
 * is the round we are waiting for; only when the active one is already running does `next` name
 * the upcoming start. `null` when nothing upcoming is known.
 */
export function nextRoundStartMs(
  view: JackpotView,
  now: number = Date.now()
): number | null {
  const activeStart = parseMs(view.events?.active?.activated);
  if (activeStart !== null && activeStart > now) return activeStart;
  return parseMs(view.events?.next?.activated);
}

/** Milliseconds until the upcoming round starts (the cooldown), or `null` when none is known. */
export function msToNextRound(
  view: JackpotView,
  now: number = Date.now()
): number | null {
  const start = nextRoundStartMs(view, now);
  if (start === null) return null;
  return Math.max(0, start - now);
}

export type TimedTier = "calm" | "soon" | "final";

/** The timed countdown's presentation tier from the time left: minutes, `m:ss`, red seconds. */
export function timedTier(msLeft: number): TimedTier {
  if (msLeft <= TIMED_FINAL_MS) return "final";
  if (msLeft <= TIMED_SOON_MS) return "soon";
  return "calm";
}

/** Milliseconds since the last payout, or `null` when this jackpot has never paid out. */
export function msSinceLastWin(
  view: JackpotView,
  now: number = Date.now()
): number | null {
  const paidAt = parseMs(view.events?.last?.payout_date);
  return paidAt === null ? null : Math.max(0, now - paidAt);
}

/** The backend caps the feed at this many rows (`RECENT_JACKPOT_WINS_LIMIT`); when every row is
 *  inside the pace window the true count is at least this, and the copy says "20+". */
export const RECENT_WINS_LIMIT = 20;
const DAY_MS = 24 * HOUR_MS;

/**
 * How many feed rows fall inside the last 24 hours, for the card's pace line — derived
 * client-side from `wonAt`, the same way the Sprint feed counts its hour. A day rather than an
 * hour because a jackpot pays out far less often than a pool hands out slots.
 */
export function winsInLastDay(
  jc: JackpotCampaign,
  now: number = Date.now()
): { count: number; plus: boolean } {
  const count = jc.recent_wins.filter((row) => {
    const wonAt = Date.parse(row.wonAt);
    return Number.isFinite(wonAt) && now - wonAt <= DAY_MS;
  }).length;
  return { count, plus: count >= RECENT_WINS_LIMIT };
}

/** The campaign's jackpots in the operator's order — the order every surface renders them in. */
export function orderedJackpots(
  campaign: PublicCampaignSchema
): JackpotSchema[] {
  return [...(campaign.jackpots ?? [])].sort(
    (a, b) => a.display_order - b.display_order
  );
}

/** The live pot with the highest value, for the in-game button. */
export function biggestLivePot(jc: JackpotCampaign): JackpotView | null {
  let best: JackpotView | null = null;
  for (const view of jc.jackpots) {
    const active = view.events?.active;
    if (!active) continue;
    if (
      !best ||
      active.jackpot_value.value > best.events!.active!.jackpot_value.value
    ) {
      best = view;
    }
  }
  return best;
}

/**
 * The jackpot a player is likeliest to see won next — for the one line the in-game button has
 * under the pot. A timed pot still running is the surest thing on the board, so the one nearest
 * its deadline wins; otherwise the pays-before pot nearest its ceiling; `null` when the board is
 * progressives only (nothing here is "closer" than anything else).
 */
export function closestToWin(
  jc: JackpotCampaign,
  now: number = Date.now()
): JackpotView | null {
  let timed: JackpotView | null = null;
  let timedLeft = Infinity;
  let paysBefore: JackpotView | null = null;
  let paysBeforeFraction = -1;
  for (const view of jc.jackpots) {
    const kind = jackpotKind(view.jackpot);
    if (kind === "TD") {
      const left = msToDeadline(view, now);
      // Past its deadline it wins on the next bet: nothing on the board is closer than that.
      if (left !== null && left <= 0) return view;
      if (left !== null && left < timedLeft) {
        timed = view;
        timedLeft = left;
      }
    } else if (kind === "PB") {
      const fraction = ceilingFraction(view);
      if (fraction !== null && fraction > paysBeforeFraction) {
        paysBefore = view;
        paysBeforeFraction = fraction;
      }
    }
  }
  return timed ?? paysBefore;
}

/**
 * The earliest wall-clock moment at which something on this board changes on its own — a timed
 * deadline, a cooldown ending, a "just won" expiring. `null` when nothing on the board depends on
 * time (a finished campaign, or nothing but progressives with no recent win). The clock hook uses
 * it to decide whether to tick per second, per minute, or not at all.
 */
export function nextTimeBoundary(
  jc: JackpotCampaign,
  now: number = Date.now()
): number | null {
  if (jc.campaign.status === "FI") return null;
  let earliest: number | null = null;
  const consider = (ms: number | null) => {
    if (ms === null || ms <= now) return;
    if (earliest === null || ms < earliest) earliest = ms;
  };
  for (const view of jc.jackpots) {
    consider(parseMs(view.events?.active?.activated));
    consider(parseMs(view.events?.active?.payout_before));
    consider(parseMs(view.events?.next?.activated));
    const paidAt = parseMs(view.events?.last?.payout_date);
    if (paidAt !== null) consider(paidAt + JUST_WON_MS);
  }
  return earliest;
}

/** Tick cadence for a board: per second inside the final hour of any boundary, per minute above
 *  it, off (`null`) when there is no boundary. */
export function clockCadenceMs(
  jc: JackpotCampaign,
  now: number = Date.now()
): number | null {
  const boundary = nextTimeBoundary(jc, now);
  if (boundary === null) return null;
  return boundary - now <= HOUR_MS ? SECOND_MS : MINUTE_MS;
}
